import { useState } from "react";
import ChevronRightIcon from "@heroicons/react/24/solid/ChevronRightIcon";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import type { FundDataWithType } from "@hsl/fund-explorer/metaSchemas";

import useFundPageURL from "../../../hooks/useFundPageURL";
import displayURLForFund from "../../../lib/displayURLForFund";
import useExplorerStore from "../../../store/store";
import SubscriptionButtons from "../../Subscription/SubscriptionButtons";
import { AboutView, TagsView } from "../views";
import { FundCardTitle } from "./FundCardTitle";

export interface FundCardHeadProps {
    fund: FundDataWithType;
    FundCardIsOpen?: boolean;
    onSubscribeButtonClick: (
        mode: "watch" | "subscribe",
    ) => (loggedIn: boolean) => void;
    isWatching: boolean;
    isSubscribed: boolean;
    hasUpdates: boolean;
    itemInBasket: boolean;
}

const FundCardHead = ({
    fund,
    FundCardIsOpen = false,
    onSubscribeButtonClick,
    isWatching,
    isSubscribed,
    hasUpdates,
    itemInBasket,
}: FundCardHeadProps) => {
    const { name, fundPageURL } = fund.data.data;
    const cardVariant =
        "card_variant" in fund.data.data
            ? fund.data.data.card_variant
            : undefined;
    const hideSubscription = [
        "Fund Group Fund Card",
        "Fund Group Card - Target Date",
    ].includes(String(cardVariant));
    const [animationActive, setAnimationActive] = useState(false);
    const groupBy = useExplorerStore((state) => state.groupBy);
    const showURL = displayURLForFund(fund.type, cardVariant);
    const url = useFundPageURL(fundPageURL);
    const { clickLinkEvent } = useYardActionEvents();

    const handleMouseEnter = () => {
        setAnimationActive(true);
    };

    const handleMouseLeave = () => {
        setAnimationActive(false);
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        clickLinkEvent({
            fund: {
                name: fund.data.data.name,
                id: fund.data.id,
                type: fund.data.type,
            },
            group:
                groupBy.length > 0
                    ? (fund.data.data[
                          groupBy as keyof typeof fund.data.data
                      ] as string)
                    : "All funds",
        });
    };

    return (
        <>
            <div
                className={cx(
                    "bg-dark-blue absolute left-0 top-0 min-w-[.25rem] transition-all duration-300",
                    animationActive || FundCardIsOpen || itemInBasket
                        ? "h-full"
                        : "h-0",
                    itemInBasket && "bg-core-yellow",
                )}
            />
            <div
                className="flex w-full justify-between gap-x-6"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="flex">
                    <div className="flex-col justify-start pr-2">
                        <ChevronRightIcon
                            width={18}
                            className={cx(
                                FundCardIsOpen && "rotate-90 transform",
                                "text-charcoal transition-transform",
                            )}
                        />
                    </div>
                    <div>
                        <FundCardTitle>
                            {url && showURL ? (
                                <a
                                    href={url}
                                    onClick={handleClick}
                                    className="hover:underline"
                                >
                                    {name}
                                </a>
                            ) : (
                                <span className="text-charcoal">{name}</span>
                            )}
                        </FundCardTitle>
                        <AboutView fund={fund} />
                    </div>
                </div>
                <div className="flex flex-col items-end justify-start">
                    <TagsView fund={fund} />
                    {!hideSubscription && (
                        <SubscriptionButtons
                            fund={fund}
                            className="mt-1.5"
                            isWatching={isWatching}
                            isSubscribed={isSubscribed}
                            hasUpdates={hasUpdates}
                            onClickWatch={onSubscribeButtonClick("watch")}
                            onClickSubscribe={onSubscribeButtonClick(
                                "subscribe",
                            )}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default FundCardHead;
